/** Back office → Academy → Sales (D-110): subscribers, trials, trial → paid conversion, revenue of
 * the month (test payments apart), renewals, by country, payment method and UTM source. */

import { useAcademySalesStatsRetrieve } from "@agencyos/api-client";
import { countryName, formatXaf } from "@agencyos/core";
import { GlassCard, Skeleton, StatCard, TextField } from "@agencyos/ui-web";
import { Banknote, FlaskConical, Percent, RefreshCw, Undo2, UserPlus, UsersRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { isObject, num, percent, str } from "../shared";

interface Row {
  key: string;
  count: number;
  revenue_xaf: number;
  test_revenue_xaf: number;
}

function rows(value: unknown): Row[] {
  return Array.isArray(value)
    ? value.filter(isObject).map((r) => ({
        key: str(r.key),
        count: num(r.count),
        revenue_xaf: num(r.revenue_xaf),
        test_revenue_xaf: num(r.test_revenue_xaf),
      }))
    : [];
}

function Breakdown({ title, data, label }: { title: string; data: Row[]; label: (key: string) => string }) {
  const { t } = useTranslation();
  return (
    <section className="stack" aria-label={title}>
      <h3>{title}</h3>
      {data.length === 0 ? (
        <p className="muted">{t("academySales.admin.empty")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{title}</th>
                <th scope="col">{t("academySales.admin.count")}</th>
                <th scope="col">{t("academySales.admin.revenueCol")}</th>
                <th scope="col">{t("academySales.admin.testCol")}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.key || "none"}>
                  <td>{label(row.key)}</td>
                  <td className="ao-num">{row.count}</td>
                  <td className="ao-num">{formatXaf(row.revenue_xaf)}</td>
                  <td className="ao-num muted">{formatXaf(row.test_revenue_xaf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function SalesTab() {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const stats = useAcademySalesStatsRetrieve({ month });
  const data = stats.data?.status === 200 && isObject(stats.data.data) ? stats.data.data : undefined;
  const country = (code: string) => (code ? countryName(code, i18n.language) : t("academySales.admin.unknown"));
  const method = (key: string) => (key ? t(`academySales.method.${key}`, { defaultValue: key }) : t("academySales.admin.unknown"));
  const source = (key: string) => key || t("academySales.admin.direct");
  const signups = rows(data?.signups_by_country);
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("academySales.admin.tab.sales")}</h2>
          <p className="muted">{t("academySales.admin.testNote")}</p>
        </div>
        <TextField label={t("academySales.admin.month")} type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <>
          <div className="as-kpis">
            <StatCard icon={<UsersRound size={18} strokeWidth={1.75} />} label={t("academySales.admin.kpi.active")} value={num(data.active_subscribers)} />
            <StatCard icon={<UserPlus size={18} strokeWidth={1.75} />} label={t("academySales.admin.kpi.newLearners")} value={num(data.new_learners)} hint={`${t("academySales.admin.kpi.trials")} : ${num(data.trials)}`} />
            <StatCard
              icon={<Percent size={18} strokeWidth={1.75} />}
              label={t("academySales.admin.kpi.rate")}
              value={percent(num(data.conversion_rate))}
              hint={`${t("academySales.admin.kpi.conversions")} : ${num(data.conversions)}`}
              tone="success"
            />
            <StatCard icon={<Banknote size={18} strokeWidth={1.75} />} label={t("academySales.admin.kpi.revenue")} value={formatXaf(num(data.revenue_xaf))} tone="success" />
            <StatCard icon={<RefreshCw size={18} strokeWidth={1.75} />} label={t("academySales.admin.kpi.renewals")} value={num(data.renewals)} />
            <StatCard icon={<FlaskConical size={18} strokeWidth={1.75} />} label={t("academySales.admin.kpi.testRevenue")} value={formatXaf(num(data.test_revenue_xaf))} tone="warning" />
            <StatCard icon={<Undo2 size={18} strokeWidth={1.75} />} label={t("academySales.admin.kpi.refunds")} value={formatXaf(num(data.refunds_xaf))} tone="danger" />
            {/* D-131: providers' fees paid by the learners, passed on to the provider: never revenue */}
            <StatCard
              icon={<Banknote size={18} strokeWidth={1.75} />}
              label={t("academySales.admin.kpi.paymentFees")}
              value={formatXaf(num(data.payment_fees_xaf))}
              hint={t("academySales.admin.kpi.paymentFeesHint")}
            />
          </div>
          <div className="as-breakdowns">
            <Breakdown title={t("academySales.admin.byCountry")} data={rows(data.by_country)} label={country} />
            <Breakdown title={t("academySales.admin.byMethod")} data={rows(data.by_method)} label={method} />
            <Breakdown title={t("academySales.admin.bySource")} data={rows(data.by_source)} label={source} />
            <section className="stack" aria-label={t("academySales.admin.signups")}>
              <h3>{t("academySales.admin.signups")}</h3>
              {signups.length === 0 ? (
                <p className="muted">{t("academySales.admin.empty")}</p>
              ) : (
                <ul className="as-bars">
                  {signups.map((row) => {
                    const max = Math.max(...signups.map((r) => r.count), 1);
                    return (
                      <li key={row.key || "none"}>
                        <span>{country(row.key)}</span>
                        <span className="as-bar" aria-hidden="true">
                          <span style={{ inlineSize: `${Math.round((row.count / max) * 100)}%` }} />
                        </span>
                        <span className="ao-num">{row.count}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </GlassCard>
  );
}
